/**
 * Login & Register Page
 * Design System v2 - Matching Landing Page Aesthetic
 * Teal + Lime color palette with glassmorphism
 */

import { ArrowRight, Eye, EyeOff, FileText, Lock, Mail, Shield, Sparkles, User, Zap } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/features/auth';
import { useSessionContext } from '@/contexts/SessionContext';
import { authAPI } from '@/utils/api';
import { logger } from '@/utils/logger';
// Note: updateUser was removed - user data is now managed via authStore.setUser()
import { notifications } from '@/lib/notifications';
import { OAuthButtonsGroup } from './OAuthButtons';
import { TwoFactorChallenge } from './TwoFactorChallenge';
import { MagicLinkRequest } from './MagicLinkRequest';

interface LoginProps {
  onLogin: (token: string, user: any) => void;
}

// Floating particles animation component
const FloatingParticles = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(6)].map((_, i) => (
      <div
        key={i}
        className="absolute w-2 h-2 bg-lime-400/30 rounded-full animate-float"
        style={{
          left: `${15 + i * 15}%`,
          top: `${20 + (i % 3) * 25}%`,
          animationDelay: `${i * 0.5}s`,
          animationDuration: `${4 + i * 0.5}s`
        }}
      />
    ))}
  </div>
);

// Feature badge component
const FeatureBadge: React.FC<{ icon: React.ElementType; text: string; delay: number }> = ({ icon: Icon, text, delay }) => (
  <div
    className="flex items-center gap-3 px-4 py-3 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 opacity-0 animate-slide-in"
    style={{ animationDelay: `${delay}ms`, animationFillMode: 'forwards' }}
  >
    <div className="w-10 h-10 rounded-xl bg-lime-500/20 flex items-center justify-center">
      <Icon size={20} className="text-lime-400" />
    </div>
    <span className="text-white/90 font-medium text-sm">{text}</span>
  </div>
);

export const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { isAuthenticated, needsCompany, setUser, setNeedsCompany } = useAuthStore();
  const { setSession } = useSessionContext();

  const [isRegister, setIsRegister] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [twoFactorChallenge, setTwoFactorChallenge] = useState<{
    challengeToken: string;
    methods: string[];
  } | null>(null);
  const [showMagicLink, setShowMagicLink] = useState(false);

  useEffect(() => {
    const registerParam = searchParams.get('register');
    if (registerParam === 'true') {
      setIsRegister(true);
    }
  }, [searchParams]);

  useEffect(() => {
    if (isAuthenticated && !loading) {
      logger.log('Already authenticated, redirecting...');
      const { user } = useAuthStore.getState();
      // SUPERADMIN → direct to superadmin dashboard
      if (user?.role === 'SUPERADMIN') {
        navigate('/superadmin');
      } else if (needsCompany) {
        navigate('/onboarding');
      } else {
        navigate('/dashboard');
      }
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation de confirmation de mot de passe pour l'inscription
    if (isRegister && formData.password !== formData.confirmPassword) {
      setError(t('auth.register.passwordMismatch'));
      return;
    }

    setLoading(true);

    try {
      const response = isRegister
        ? await authAPI.register(formData.email, formData.password, formData.fullName || formData.username)
        : await authAPI.login(formData.username, formData.password);

      logger.log('Login response:', response);

      // Check if 2FA is required
      if ('requiresTwoFactor' in response && response.requiresTwoFactor) {
        logger.log('2FA required, showing challenge');
        setTwoFactorChallenge({
          challengeToken: response.challengeToken,
          methods: response.methods || ['TOTP'],
        });
        setLoading(false);
        return;
      }

      const { user, accessToken, refreshToken, expiresIn } = response;

      logger.log('🔐 Login: Auth response received', {
        hasAccessToken: !!accessToken,
        hasRefreshToken: !!refreshToken,
        expiresIn,
      });

      const adaptedUser = {
        id: user.id as any,
        username: user.username || user.fullName || user.email,
        email: user.email,
        role: user.role,
        companyId: user.companyId || null,
        tenantId: user.tenantId,
      };

      setSession(
        accessToken,
        refreshToken || 'HTTPONLY_COOKIE',
        expiresIn || 900,
        user
      );

      logger.log('🔐 Login: setSession called, verifying token storage...');

      // Import getAccessToken to verify token was stored
      const { getAccessToken } = await import('@/services/tokenManager');
      const storedToken = getAccessToken();
      logger.log('🔐 Login: Token verification', {
        tokenStoredSuccessfully: !!storedToken,
        tokenMatches: storedToken === accessToken,
      });

      try {
        logger.log('Reloading user data...');
        const freshUserData = await authAPI.me();

        if (freshUserData) {
          const hasCompany = !!(freshUserData.companyId && freshUserData.companyId !== null);

          logger.log('User reloaded:', {
            id: freshUserData.id,
            email: freshUserData.email,
            companyId: freshUserData.companyId,
            hasCompany
          });

          setUser(freshUserData);
          setNeedsCompany(!hasCompany);

          logger.log('Calling onLogin...');
          onLogin(accessToken, freshUserData as any);

          // SUPERADMIN → direct to superadmin dashboard (bypass onboarding)
          let redirectUrl: string;
          if (freshUserData.role === 'SUPERADMIN') {
            redirectUrl = '/superadmin';
          } else if (hasCompany) {
            redirectUrl = '/dashboard';
          } else {
            redirectUrl = '/onboarding';
          }
          logger.log(`Redirecting to ${redirectUrl}...`);

          setLoading(false);
          navigate(redirectUrl, { replace: true });
          return;
        } else {
          logger.error('No user data in response');
          setError(t('auth.login.userDataError'));
          setLoading(false);
        }
      } catch (reloadError) {
        logger.error('Error reloading user:', reloadError);
        setUser(user as any);
        setNeedsCompany(!user.companyId);
        onLogin(accessToken, adaptedUser);

        // SUPERADMIN → direct to superadmin dashboard (bypass onboarding)
        let redirectUrl: string;
        if (user.role === 'SUPERADMIN') {
          redirectUrl = '/superadmin';
        } else if (user.companyId) {
          redirectUrl = '/dashboard';
        } else {
          redirectUrl = '/onboarding';
        }
        logger.log(`Fallback redirect to ${redirectUrl}...`);
        setLoading(false);
        navigate(redirectUrl, { replace: true });
        return;
      }
    } catch (err: any) {
      logger.error('Login error:', err);

      notifications.error(
        err.message || t('auth.login.genericError'),
        isRegister ? t('auth.register.errorTitle') : t('auth.login.errorTitle')
      );

      setError(err.message || t('auth.login.genericError'));
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Handle successful 2FA verification
  const handle2FASuccess = async (response: {
    accessToken: string;
    refreshToken: string;
    user: any;
    expiresIn: number;
  }) => {
    logger.log('2FA verification successful', response);

    const { user, accessToken, refreshToken, expiresIn } = response;

    setSession(
      accessToken,
      refreshToken || 'HTTPONLY_COOKIE',
      expiresIn || 900,
      user
    );

    try {
      const freshUserData = await authAPI.me();

      if (freshUserData) {
        const hasCompany = !!(freshUserData.companyId && freshUserData.companyId !== null);

        setUser(freshUserData);
        setNeedsCompany(!hasCompany);

        onLogin(accessToken, freshUserData as any);

        // SUPERADMIN → direct to superadmin dashboard (bypass onboarding)
        let redirectUrl: string;
        if (freshUserData.role === 'SUPERADMIN') {
          redirectUrl = '/superadmin';
        } else if (hasCompany) {
          redirectUrl = '/dashboard';
        } else {
          redirectUrl = '/onboarding';
        }
        navigate(redirectUrl, { replace: true });
      }
    } catch (reloadError) {
      logger.error('Error reloading user after 2FA:', reloadError);
      setUser(user);
      setNeedsCompany(!user.companyId);
      onLogin(accessToken, user);

      // SUPERADMIN → direct to superadmin dashboard (bypass onboarding)
      let redirectUrl: string;
      if (user.role === 'SUPERADMIN') {
        redirectUrl = '/superadmin';
      } else if (user.companyId) {
        redirectUrl = '/dashboard';
      } else {
        redirectUrl = '/onboarding';
      }
      navigate(redirectUrl, { replace: true });
    }

    setTwoFactorChallenge(null);
  };

  // Handle 2FA cancel
  const handle2FACancel = () => {
    setTwoFactorChallenge(null);
    setError('');
    setFormData({ ...formData, password: '' });
  };

  const inputClasses = (fieldName: string) => `
    w-full pl-12 pr-4 py-4 text-base bg-white/80 backdrop-blur-sm
    border-2 rounded-2xl transition-all duration-300 outline-none
    ${focusedField === fieldName
      ? 'border-teal-500 bg-white shadow-lg shadow-teal-500/10'
      : 'border-gray-200 hover:border-teal-300'
    }
    placeholder:text-gray-400
  `;

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800 overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(212,255,0,0.15),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(212,255,0,0.1),transparent_50%)]" />
        <div className="absolute inset-0 opacity-[0.03] bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')]" />

        <FloatingParticles />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          {/* Logo */}
          <div
            className="flex items-center gap-3 opacity-0 animate-slide-in"
            style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}
          >
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-lime-400 to-lime-500 flex items-center justify-center shadow-lg shadow-lime-500/30">
              <FileText size={28} strokeWidth={2.5} className="text-teal-700" />
            </div>
            <span className="text-white text-3xl font-bold tracking-tight">FacturXPro</span>
          </div>

          {/* Main Content */}
          <div className="space-y-8">
            <div>
              <h1
                className="text-5xl font-bold text-white leading-tight mb-4 opacity-0 animate-slide-in"
                style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}
              >
                {isRegister ? (
                  <>{t('auth.login.joinTitle')}<br /><span className="text-lime-400">{t('auth.login.joinHighlight')}</span></>
                ) : (
                  <>{t('auth.login.welcomeTitle')}<br /><span className="text-lime-400">{t('auth.login.welcomeHighlight')}</span></>
                )}
              </h1>
              <p
                className="text-xl text-white/70 leading-relaxed max-w-md opacity-0 animate-slide-in"
                style={{ animationDelay: '300ms', animationFillMode: 'forwards' }}
              >
                {isRegister
                  ? t('auth.register.subtitle')
                  : t('auth.login.subtitle')
                }
              </p>
            </div>

            {/* Feature Badges */}
            <div className="space-y-3">
              <FeatureBadge icon={Shield} text={t('auth.login.feature1')} delay={400} />
              <FeatureBadge icon={Zap} text={t('auth.login.feature2')} delay={500} />
              <FeatureBadge icon={Sparkles} text={t('auth.login.feature3')} delay={600} />
            </div>
          </div>

          {/* Bottom Stats */}
          <div
            className="flex items-center gap-8 opacity-0 animate-slide-in"
            style={{ animationDelay: '700ms', animationFillMode: 'forwards' }}
          >
            <div>
              <div className="text-3xl font-bold text-lime-400">51%</div>
              <div className="text-white/60 text-sm">{t('auth.login.stat1')}</div>
            </div>
            <div className="w-px h-12 bg-white/20" />
            <div>
              <div className="text-3xl font-bold text-lime-400">10 ans</div>
              <div className="text-white/60 text-sm">{t('auth.login.stat2')}</div>
            </div>
            <div className="w-px h-12 bg-white/20" />
            <div>
              <div className="text-3xl font-bold text-lime-400">24h</div>
              <div className="text-white/60 text-sm">{t('auth.login.stat3')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-8 lg:p-12 bg-gradient-to-br from-gray-50 to-white relative">
        {/* 2FA Challenge */}
        {twoFactorChallenge && (
          <>
            {/* Mobile Logo */}
            <div className="absolute top-6 left-6 lg:hidden">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-lime-400 to-lime-500 flex items-center justify-center shadow-md">
                  <FileText size={20} strokeWidth={2.5} className="text-teal-700" />
                </div>
                <span className="text-teal-700 text-xl font-bold">FacturXPro</span>
              </div>
            </div>
            <TwoFactorChallenge
              challengeToken={twoFactorChallenge.challengeToken}
              methods={twoFactorChallenge.methods}
              onSuccess={handle2FASuccess}
              onCancel={handle2FACancel}
            />
          </>
        )}

        {/* Magic Link Form */}
        {!twoFactorChallenge && showMagicLink && (
          <>
            {/* Mobile Logo */}
            <div className="absolute top-6 left-6 lg:hidden">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-lime-400 to-lime-500 flex items-center justify-center shadow-md">
                  <FileText size={20} strokeWidth={2.5} className="text-teal-700" />
                </div>
                <span className="text-teal-700 text-xl font-bold">FacturXPro</span>
              </div>
            </div>
            <MagicLinkRequest
              onBack={() => setShowMagicLink(false)}
              defaultEmail={formData.username}
            />
          </>
        )}

        {/* Regular Login Form */}
        {!twoFactorChallenge && !showMagicLink && (
          <>
        {/* Mobile Logo */}
        <div className="absolute top-6 left-6 lg:hidden">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-lime-400 to-lime-500 flex items-center justify-center shadow-md">
              <FileText size={20} strokeWidth={2.5} className="text-teal-700" />
            </div>
            <span className="text-teal-700 text-xl font-bold">FacturXPro</span>
          </div>
        </div>

        {/* Back to Landing */}
        <button
          onClick={() => navigate('/landing')}
          className="absolute top-6 right-6 flex items-center gap-2 text-gray-500 hover:text-teal-600 transition-colors text-sm font-medium"
        >
          <span>{t('common.actions.back')}</span>
        </button>

        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-8 lg:text-left">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              {isRegister ? t('auth.register.title') : t('auth.login.title')}
            </h2>
            <p className="text-gray-500">
              {isRegister
                ? t('auth.register.trialText')
                : t('auth.login.connectText')
              }
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-2xl text-red-600 text-sm flex items-start gap-3">
              <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-red-500 text-xs font-bold">!</span>
              </div>
              <span>{error}</span>
            </div>
          )}

          {/* OAuth Buttons */}
          <div className="mb-6">
            <OAuthButtonsGroup mode={isRegister ? 'login' : 'login'} />
          </div>

          {/* Divider */}
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-gradient-to-br from-gray-50 to-white text-gray-500">
                {t('auth.login.orWithEmail')}
              </span>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email/Username Field */}
            <div className="relative">
              <label htmlFor="username" className="block text-sm font-semibold text-gray-700 mb-2">
                {isRegister ? t('auth.fields.username') : t('auth.fields.emailAddress')}
              </label>
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                  {isRegister ? <User size={20} /> : <Mail size={20} />}
                </div>
                <input
                  type={isRegister ? 'text' : 'email'}
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('username')}
                  onBlur={() => setFocusedField(null)}
                  required
                  className={inputClasses('username')}
                  placeholder={isRegister ? t('auth.login.placeholders.username') : t('auth.login.placeholders.email')}
                />
              </div>
            </div>

            {/* Register-only fields */}
            {isRegister && (
              <>
                <div className="relative">
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('auth.fields.emailAddress')}
                  </label>
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                      <Mail size={20} />
                    </div>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('email')}
                      onBlur={() => setFocusedField(null)}
                      required
                      className={inputClasses('email')}
                      placeholder={t('auth.login.placeholders.email')}
                    />
                  </div>
                </div>

                <div className="relative">
                  <label htmlFor="fullName" className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('auth.fields.fullName')} <span className="text-gray-400 font-normal">({t('common.optional')})</span>
                  </label>
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                      <User size={20} />
                    </div>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('fullName')}
                      onBlur={() => setFocusedField(null)}
                      className={inputClasses('fullName')}
                      placeholder={t('auth.login.placeholders.fullName')}
                    />
                  </div>
                </div>
              </>
            )}

            {/* Password Field */}
            <div className="relative">
              <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                {t('auth.fields.password')}
              </label>
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                  <Lock size={20} />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('password')}
                  onBlur={() => setFocusedField(null)}
                  required
                  minLength={12}
                  className={`${inputClasses('password')} pr-12`}
                  placeholder="••••••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-teal-600 transition-colors"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {isRegister && (
                <p className="text-xs text-gray-500 mt-2 ml-1">{t('auth.register.minChars', { count: 12 })}</p>
              )}
            </div>

            {/* Confirm Password Field (Register only) */}
            {isRegister && (
              <div className="relative">
                <label htmlFor="confirmPassword" className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('auth.fields.confirmPassword')}
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                    <Lock size={20} />
                  </div>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('confirmPassword')}
                    onBlur={() => setFocusedField(null)}
                    required
                    minLength={12}
                    className={`${inputClasses('confirmPassword')} pr-12`}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-teal-600 transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                  <p className="text-xs text-red-500 mt-2 ml-1">{t('auth.register.passwordMismatch')}</p>
                )}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 mt-2 bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white font-bold text-base rounded-2xl transition-all duration-300 disabled:opacity-50 shadow-lg shadow-teal-500/30 hover:shadow-xl hover:shadow-teal-500/40 hover:-translate-y-0.5 active:scale-[0.98] flex items-center justify-center gap-3 group"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                  <span>{t('auth.login.loading')}</span>
                </>
              ) : (
                <>
                  <span>{isRegister ? t('auth.register.button') : t('auth.login.button')}</span>
                  <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>

            {/* Toggle Login/Register */}
            <button
              type="button"
              onClick={() => {
                setIsRegister(!isRegister);
                setError('');
                setFormData({ username: '', email: '', password: '', confirmPassword: '', fullName: '' });
              }}
              className="w-full py-4 border-2 border-gray-200 text-gray-700 font-semibold rounded-2xl hover:border-teal-500 hover:text-teal-600 hover:bg-teal-50/50 transition-all duration-300"
            >
              {isRegister ? t('auth.register.alreadyHaveAccount') : t('auth.login.noAccount')}
            </button>

            {/* Magic Link Option (Login only) */}
            {!isRegister && (
              <button
                type="button"
                onClick={() => setShowMagicLink(true)}
                className="w-full mt-3 py-3 text-gray-500 hover:text-teal-600 font-medium text-sm transition-colors duration-300 flex items-center justify-center gap-2"
              >
                <Mail size={16} />
                <span>{t('auth.login.passwordless')}</span>
              </button>
            )}
          </form>


          {/* Footer */}
          <p className="text-center text-xs text-gray-400 mt-8">
            {t('auth.login.termsPrefix')}{' '}
            <a href="#" className="text-teal-600 hover:underline">{t('auth.login.termsOfService')}</a>
            {' '}{t('auth.login.termsAnd')}{' '}
            <a href="#" className="text-teal-600 hover:underline">{t('auth.login.privacyPolicy')}</a>
          </p>
        </div>
          </>
        )}
      </div>

      {/* Custom Animations */}
      <style>{`
        @keyframes slide-in {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0) scale(1);
            opacity: 0.3;
          }
          50% {
            transform: translateY(-20px) scale(1.2);
            opacity: 0.6;
          }
        }

        .animate-slide-in {
          animation: slide-in 0.6s ease-out;
        }

        .animate-float {
          animation: float 4s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};
